import { fetchWithDeadline, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import {
  ACTOR_SYNC_HEAD_HEADER,
  ACTOR_SYNC_IMPORT_CHANGES_PER_PART,
  ACTOR_SYNC_PART_HEADER,
  ACTOR_SYNC_PROTOCOL,
  ACTOR_SYNC_PROTOCOL_HEADER,
  ACTOR_SYNC_REFUSAL_HEADER,
  ACTOR_SYNC_SET_ASIDE_HEADER,
  type ActorJournalHead,
  type ActorSyncImportChange,
  formatActorJournalHead,
} from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";
import { type ActorSyncRefusal, ActorSyncRefusedError } from "#actor-sync-refusal";

/**
 * Send a replica's changes to the import endpoint (see `database.md`, "Server: import
 * endpoint"), in parts the gateway accepts, in order. The import is named by `importId`: a part
 * that fails ends the import with the gateway's reason, and sending the whole import again later
 * is always safe, because the gateway skips what it already applied.
 */
export interface ActorSyncImportResult {
  applied: number;
  setAside: number;
}

const TRAILING_SLASH = /\/$/;
const REFUSALS: readonly ActorSyncRefusal[] = [
  "credential",
  "database-moved",
  "server-behind",
  "unavailable",
  "update-required",
];
const TOO_LARGE_STATUS = 413;

function readRefusal(response: Response): ActorSyncRefusal {
  const header = response.headers.get(ACTOR_SYNC_REFUSAL_HEADER);
  return (
    REFUSALS.find((refusal) => refusal === header) ??
    (response.status === TOO_LARGE_STATUS ? "too-large" : "unavailable")
  );
}

export async function sendActorSyncImport({
  authToken,
  changes,
  clientId,
  gatewayUrl,
  importId,
  journalHead,
}: {
  authToken: string;
  changes: readonly ActorSyncImportChange[];
  clientId: string | null;
  gatewayUrl: string;
  importId: string;
  journalHead: ActorJournalHead;
}): Promise<ActorSyncImportResult> {
  const parts: ActorSyncImportChange[][] = [];
  for (let offset = 0; offset < changes.length; offset += ACTOR_SYNC_IMPORT_CHANGES_PER_PART) {
    parts.push(changes.slice(offset, offset + ACTOR_SYNC_IMPORT_CHANGES_PER_PART));
  }
  if (parts.length === 0) {
    // Nothing to send still completes the import, so a repeat finds it done.
    parts.push([]);
  }
  const url = `${gatewayUrl.replace(TRAILING_SLASH, "")}/import`;
  const result: ActorSyncImportResult = { applied: 0, setAside: 0 };
  for (const [index, part] of parts.entries()) {
    const response = await fetchWithDeadline(url, {
      body: JSON.stringify({ changes: part, clientId, importId }),
      bufferBody: true,
      deadline: 120_000,
      headers: {
        [ACTOR_SYNC_HEAD_HEADER]: formatActorJournalHead(journalHead),
        [ACTOR_SYNC_PART_HEADER]: `${index}/${parts.length}`,
        [ACTOR_SYNC_PROTOCOL_HEADER]: String(ACTOR_SYNC_PROTOCOL),
        authorization: `Bearer ${authToken}`,
        "content-type": "application/json",
      },
      method: "POST",
      operation: "Cloud sync import",
    });
    if (!response.ok) {
      const detail = await withDeadline(
        { deadline: 10_000, operation: "Reading the refusal" },
        () => response.text(),
      ).catch(() => "");
      throw new ActorSyncRefusedError(
        readRefusal(response),
        new Error(`The import was refused (${response.status}): ${detail.slice(0, 200)}`),
      );
    }
    const payload: unknown = await withDeadline(
      { deadline: 10_000, operation: "Reading the import answer" },
      () => response.json(),
    );
    const applied = Number(
      typeof payload === "object" && payload !== null ? Reflect.get(payload, "applied") : 0,
    );
    result.applied += Number.isFinite(applied) ? applied : 0;
    result.setAside += Number(response.headers.get(ACTOR_SYNC_SET_ASIDE_HEADER) ?? 0) || 0;
  }
  return result;
}
